import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Badge } from 'antd';
import {
  BorderOutlined,
  AppstoreOutlined,
  SwapOutlined,
  PieChartOutlined,
  EllipsisOutlined,
} from '@ant-design/icons';
import { BOTTOM_NAV_HEIGHT } from '../../constants/mobile';
import { useSupportPanel } from '../SupportWidget/SupportProvider';
import MoreSheet from './MoreSheet';

interface TabItem {
  key: string;
  label: string;
  icon: React.ReactNode;
  /** Routes that light this tab up. The first one is where a tap navigates. */
  paths: string[];
  /** The raised round button in the middle of the bar. */
  center?: boolean;
}

/**
 * The three strategies either side of the strategy list (the landing), which sits in the
 * middle as a raised round button. Everything else lives behind More, so the bar stays at
 * five targets.
 */
const TABS: TabItem[] = [
  { key: 'grid', label: 'Grid', icon: <BorderOutlined />, paths: ['/grid-trading'] },
  { key: 'pair', label: 'Pair', icon: <SwapOutlined />, paths: ['/pair-trading'] },
  { key: 'strategies', label: 'Strategies', icon: <AppstoreOutlined />, paths: ['/home'], center: true },
  { key: 'smart-beta', label: 'Smart Beta', icon: <PieChartOutlined />, paths: ['/smart-beta'] },
];

/** Routes reachable only through the More sheet — they keep More highlighted. */
const MORE_PATHS = ['/portfolio', '/tournament', '/price-board', '/executions', '/ai', '/docs', '/settings'];

const ACTIVE = '#0ECB81';
const IDLE = '#848E9C';

/**
 * Bottom navigation for the compact shell, replacing the desktop left sidebar.
 * Renders inside the layout column (not `position: fixed`) so page content is laid
 * out above it rather than underneath, and no page needs bottom padding.
 */
const BottomTabBar: React.FC = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [moreOpen, setMoreOpen] = React.useState(false);
  const { unread } = useSupportPanel();

  const moreActive = MORE_PATHS.includes(pathname);

  return (
    <>
      <nav
        aria-label="Main"
        data-testid="mobile-tab-bar"
        style={{
          flexShrink: 0,
          display: 'grid',
          gridTemplateColumns: 'repeat(5, 1fr)',
          background: '#1A1D23',
          borderTop: '1px solid #2B3139',
          paddingBottom: 'env(safe-area-inset-bottom)',
          zIndex: 50,
        }}
      >
        {TABS.map((tab) => {
          const active = tab.paths.includes(pathname);
          if (tab.center) {
            return (
              <button
                key={tab.key}
                type="button"
                onClick={() => navigate(tab.paths[0])}
                aria-current={active ? 'page' : undefined}
                style={{ ...tabButtonStyle, color: active ? ACTIVE : IDLE }}
              >
                <span
                  style={{
                    ...centerCircleStyle,
                    background: active ? ACTIVE : '#2B3139',
                    color: active ? '#0B0E11' : '#EAECEF',
                    borderColor: active ? ACTIVE : '#3d4148',
                  }}
                >
                  {tab.icon}
                </span>
                <span style={labelStyle}>{tab.label}</span>
              </button>
            );
          }
          return (
            <button
              key={tab.key}
              type="button"
              onClick={() => navigate(tab.paths[0])}
              aria-current={active ? 'page' : undefined}
              style={{ ...tabButtonStyle, color: active ? ACTIVE : IDLE }}
            >
              <span style={{ fontSize: 18, lineHeight: 1 }}>{tab.icon}</span>
              <span style={labelStyle}>{tab.label}</span>
            </button>
          );
        })}
        <button
          type="button"
          onClick={() => setMoreOpen(true)}
          aria-label="More"
          aria-expanded={moreOpen}
          style={{ ...tabButtonStyle, color: moreActive || moreOpen ? ACTIVE : IDLE }}
        >
          <span style={{ fontSize: 18, lineHeight: 1 }}>
            {/* The unread count lives on More because that is where Support now sits —
                without it a reply on a closed sheet is invisible. */}
            <Badge dot={unread > 0} offset={[2, 0]}>
              <EllipsisOutlined style={{ color: 'inherit', fontSize: 18 }} />
            </Badge>
          </span>
          <span style={labelStyle}>More</span>
        </button>
      </nav>
      <MoreSheet open={moreOpen} onClose={() => setMoreOpen(false)} />
    </>
  );
};

const tabButtonStyle: React.CSSProperties = {
  height: BOTTOM_NAV_HEIGHT,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 3,
  background: 'transparent',
  border: 'none',
  padding: 0,
  cursor: 'pointer',
  // iOS otherwise flashes a grey box over the whole tab on tap.
  WebkitTapHighlightColor: 'transparent',
  transition: 'color 0.15s',
};

// Lifted half out of the bar so the strategy list reads as the hub of the app.
const centerCircleStyle: React.CSSProperties = {
  width: 44,
  height: 44,
  marginTop: -18,
  borderRadius: '50%',
  border: '2px solid',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: 20,
  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.45)',
  transition: 'background 0.15s, color 0.15s',
};

const labelStyle: React.CSSProperties = {
  fontSize: 9.5,
  fontWeight: 600,
  lineHeight: 1,
  letterSpacing: '-0.2px',
  whiteSpace: 'nowrap',
};

export default BottomTabBar;
